import { unref } from 'vue';
import { ElMessageBox } from 'element-plus/es';
import { API_BASE_URL, LAYOUT_PATH } from '@/config/setting';
import axios from 'axios';
import router from '@/router';

import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { Action } from 'element-plus';
import type { R } from '@/api';

import { getToken, setToken } from './token-util';
import { logout, toURLSearch } from './common';
import LzdUtil from './lzd-util';
import LzdTTLCache from './lzd-ttl-cache';
import { encrypt } from './lzd-hybrid-encrypt-util';

/** 密钥缓存 */
const PUBLIC_KEY_CACHE = new LzdTTLCache<string>();

/** 获取RSA公钥 */
async function queryPublicKeyByCache() {
  const publicKeyStr = PUBLIC_KEY_CACHE.get('PUBLIC_KEY');
  if (publicKeyStr) {
    return publicKeyStr;
  }
  const result = await LzdUtil.queryPublicKey();
  if (result) {
    PUBLIC_KEY_CACHE.set('PUBLIC_KEY', result);
  }
  return result;
}

/**
 * axios 实例封装
 */
const service = axios.create({
  baseURL: API_BASE_URL
});

/**
 * 添加请求拦截器
 */
service.interceptors.request.use(
  (config) => {
    requestInterceptor(config);
    return config;
  },
  (error) => {
    console.error(error);
    return Promise.reject(new Error('网络错误'));
  }
);

/**
 * 添加响应拦截器
 */
service.interceptors.response.use(
  (res: AxiosResponse<R<unknown>>) => {
    const errorMessage = responseInterceptor(res);
    if (errorMessage) {
      return Promise.reject(new Error(errorMessage));
    }
    return res;
  },
  async (error) => {
    console.error(error);
    // return Promise.reject(new Error('网络错误'));
    const config = error.config;
    // 如果没有配置或已重试3次，直接抛出错误
    if (!config || config?.method !== 'post' || config._retryCount >= 3) {
      // return Promise.reject(error);
      return Promise.reject(new Error('网络错误'));
    }

    // 初始化重试次数
    config._retryCount = config._retryCount || 0;
    config._retryCount += 1;

    // 等待一段时间再重试（可选：指数退避）
    const delay = new Promise((resolve) =>
      setTimeout(resolve, 3000 * config._retryCount)
    );

    // 等待延迟后重新发起请求
    await delay;
    return service(config);
  }
);

/**
 * 请求拦截处理
 */
function requestInterceptor(config: InternalAxiosRequestConfig<any>) {
  // 添加token到header
  const token = getToken();
  if (token && config.headers) {
    config.headers['Authorization'] = token;
  }

  // 添加当前角色到请求参数中
  const data = config.params ?? config.data ?? {};
  if (typeof data === 'object') {
    // RP 格式参数
    if ('where' in data) {
      // 禁止填充 appId、dataScopeId、roleCode
      const disabled = data.disabledFillCurrInfo;
      delete data.disabledFillCurrInfo;
      if (!disabled) {
        LzdUtil.dataFillCurrInfo(data);
      }
    }
    // 添加和导入
    else if (
      config.method === 'post' &&
      config.data &&
      config.url &&
      (config.url.endsWith('insert') || config.url.endsWith('import'))
    ) {
      const isFormData = config.data instanceof FormData;
      // 禁止填充 appId、dataScopeId、roleCode
      const disabled = !isFormData
        ? data.disabledFillCurrInfo
        : data.get('disabledFillCurrInfo') === 'true';
      delete data.disabledFillCurrInfo;
      // 为禁止
      if (!disabled) {
        if (isFormData) {
          LzdUtil.formDataFillCurrInfo(config.data);
        } else {
          LzdUtil.dataFillCurrInfo(config.data);
        }
      }
    }

    // 删除是否填充标识
    if (data instanceof FormData) {
      data.delete('disabledFillCurrInfo');
    } else if (typeof data === 'object') {
      delete data.disabledFillCurrInfo;
    }
  }

  // get请求处理数组和对象类型参数
  if (config.method === 'get' && config.params) {
    config.url = toURLSearch(config.params, config.url);
    config.params = {};
  }
}

function tipsLogout() {
  const { path } = unref(router.currentRoute);
  if (path == LAYOUT_PATH) {
    logout(true, void 0, router.push);
  } else if (path !== '/login') {
    ElMessageBox.close();
    ElMessageBox.alert('登录状态已过期, 请退出重新登录!', '系统提示', {
      confirmButtonText: '重新登录',
      callback: (action: Action) => {
        if (action === 'confirm') {
          logout(false, void 0, router.push);
        }
      },
      type: 'warning',
      draggable: true
    });
  }
}

/**
 * 响应拦截处理
 */
function responseInterceptor<T>(res: AxiosResponse<R<T>>) {
  // 登录过期处理
  if (res.data?.code === 401) {
    tipsLogout();
    return res.data.message;
  }
  // 续期token
  const newToken = res.headers['authorization'];
  if (newToken) {
    setToken(newToken);
    // 再次加载用户信息
    setTimeout(() => {
      globalThis.__lzd_reload_user_info?.();
    }, 500);
  }
}

/**
 * GET 请求
 * @param url 请求地址
 * @param data 请求参数
 * @param disabled 禁止填充 appId、dataScopeId、roleCode
 * @param showErrMsg 显示错误信息
 */
async function get<T>(
  url: string,
  data?: Record<string, any>,
  disabled?: boolean,
  showErrMsg?: boolean
) {
  try {
    data = data ?? {};
    // 禁止填充标识
    data.disabledFillCurrInfo = !!disabled;
    const result = await service.get<R<T>>(url, { params: data });
    if (showErrMsg && result.data.code !== 0) {
      LzdUtil.tipsError(result.data);
    }
    return LzdUtil.requestResult<T>(result);
  } finally {
    track();
  }
}

/**
 * POST 请求
 * @param url 请求地址
 * @param data 请求参数
 * @param disabled 禁止填充 appId、dataScopeId、roleCode
 * @param showErrMsg 显示错误信息
 */
async function post<T>(
  url: string,
  data?: Record<string, any> | FormData,
  disabled?: boolean,
  showErrMsg?: boolean
) {
  // if (!(data instanceof FormData)) {
  //   return postAes(url, data, disabled, showErrMsg);
  // }

  try {
    data = data ?? {};
    if (!(data instanceof FormData)) {
      // 禁止填充标识
      data.disabledFillCurrInfo = !!disabled;
    } else {
      data.set('disabledFillCurrInfo', String(!!disabled));
    }
    const result = await service.post<R<T>>(url, data);
    if (showErrMsg && result.data.code !== 0) {
      if (result.data.code === 500) {
        LzdUtil.tipsConfirm(LzdUtil.tipsMessageBuilder(result.data), {
          showCancelButton: false,
          closeOnClickModal: false
        });
      } else {
        LzdUtil.tipsError(result.data);
      }
    }
    return LzdUtil.requestResult<T>(result).catch((v) => v as R<T>);
  } finally {
    track();
  }
}

/**
 * RSA + AES 混合加/解密 POST 请求
 * @param url 请求地址
 * @param data 请求参数
 * @param disabled 禁止填充 appId、dataScopeId、roleCode
 * @param showErrMsg 显示错误信息
 */
async function postEncrypt<T>(
  url: string,
  data?: Record<string, any>,
  disabled?: boolean,
  showErrMsg?: boolean
) {
  try {
    data = data ?? {};
    if (!disabled && 'where' in data && Array.isArray(data.where)) {
      LzdUtil.dataFillCurrInfo(data);
    }

    const sk = await queryPublicKeyByCache();
    const payload = await encrypt(data, sk!);

    const token = getToken()!;
    const result = fetch(API_BASE_URL + url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
        'Lzd-Encrypted': 'on'
      }
    })
      .then((v) => {
        if (!v.ok) {
          return { code: 1, message: v.statusText };
        }
        // 续期token
        const newToken = v.headers.get('Authorization');
        if (newToken) {
          setToken(newToken);
          // 再次加载用户信息
          setTimeout(() => {
            globalThis.__lzd_reload_user_info?.();
          }, 500);
        }
        return v.json();
      })
      .then((v) => {
        if (v?.code === 401) {
          tipsLogout();
        } else if (showErrMsg && v?.code !== 0) {
          if (v.code === 500) {
            LzdUtil.tipsConfirm(LzdUtil.tipsMessageBuilder(v), {
              showCancelButton: false,
              closeOnClickModal: false
            });
          } else {
            LzdUtil.tipsError(v);
          }
        }
        return v;
      })
      .catch((err) => {
        console.error(err);
        return { code: 1, message: '网络错误' };
      });

    return result as Promise<T>;
  } finally {
    track();
  }
}

let trackTimer: NodeJS.Timeout | null = null;
/** 在线心跳 请求 */
function track() {
  trackTimer && clearTimeout(trackTimer);
  trackTimer = setTimeout(
    () => {
      if (!getToken()) {
        return;
      }
      post('/bsl/tenant/auth/track', {}, true, false);
    },
    1000 * 60 * 3
  );
}

/** 数据请求 */
class LzdAjax {
  static get = get;
  static post = post;
  static postEncrypt = postEncrypt;
}

export default LzdAjax;
